import { Col, Divider, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import { type AudiFinancingModuleSpecsFragment } from '../../../../../api/fragments';
import {
    CreateAudiFinancingModuleDocument,
    type CreateAudiFinancingModuleMutation,
    type CreateAudiFinancingModuleMutationVariables,
} from '../../../../../api/mutations/createAudiFinancingModule';
import { type AudiFinancingModuleSettings, type SalesForceSettingInput } from '../../../../../api/types';
import InputField from '../../../../../components/fields/InputField';
import PasswordField from '../../../../../components/fields/PasswordField';
import SelectField from '../../../../../components/fields/SelectField';
import SingleSecretUploadField from '../../../../../components/fields/SingleSecretUploadField';
import validators from '../../../../../utilities/validators';
import { type CompanySpecs } from '../../../AddModulePage/getAvailableModules';
import AudiFinancingModulePage, { type AudiFinancingModulePageProps } from '../../AudiFinancingModulePage';
import useModuleOptions from './shared/useModuleOptions';
import { type ModuleImplementation } from './types';

type AudiFinancingModuleImplementation = ModuleImplementation<
    AudiFinancingModuleSpecsFragment,
    AudiFinancingModuleSettings & SalesForceSettingInput
>;

const formValidator = validators.compose(
    validators.requiredNonEmptyString('displayName'),
    validators.requiredString('bankModuleId'),
    validators.requiredString('baseUrl'),
    validators.requiredString('clientId'),
    validators.requiredString('clientSecret'),
    validators.requiredString('grantType')
);

const colSpan = { md: 12, xs: 24 };

type FormProps = {
    company: CompanySpecs;
};

const Form: AudiFinancingModuleImplementation['FormComponent'] = ({ company }: FormProps) => {
    const { t } = useTranslation(['moduleDetails']);
    const options = useModuleOptions(company);

    return (
        <>
            <Row gutter={10}>
                <Col {...colSpan}>
                    <InputField
                        {...t('moduleDetails:fields.displayName', { returnObjects: true })}
                        name="displayName"
                        required
                    />
                </Col>

                <Col {...colSpan}>
                    <SelectField
                        {...t('moduleDetails:fields.bankModuleId', { returnObjects: true })}
                        name="bankModuleId"
                        options={options.financing}
                        required
                        showSearch
                    />
                </Col>
            </Row>
            <Divider>{t('moduleDetails:sections.sdmModule.salesForce')}</Divider>
            <Row gutter={10}>
                <Col {...colSpan}>
                    <InputField
                        {...t('moduleDetails:fields.baseUrl', { returnObjects: true })}
                        name="baseUrl"
                        required
                    />
                </Col>
                <Col {...colSpan}>
                    <InputField
                        {...t('moduleDetails:fields.baseInsuranceUrl', { returnObjects: true })}
                        name="baseInsuranceUrl"
                    />
                </Col>
                <Col {...colSpan}>
                    <InputField
                        {...t('moduleDetails:fields.clientId', { returnObjects: true })}
                        name="clientId"
                        required
                    />
                </Col>

                <Col {...colSpan}>
                    <PasswordField
                        {...t('moduleDetails:fields.clientSecret', { returnObjects: true })}
                        name="clientSecret"
                        required
                    />
                </Col>

                <Col {...colSpan}>
                    <InputField
                        {...t('moduleDetails:fields.grantType', { returnObjects: true })}
                        name="grantType"
                        required
                    />
                </Col>
                <Col {...colSpan}>
                    <SingleSecretUploadField
                        {...t('moduleDetails:fields.authority', { returnObjects: true })}
                        name="authority"
                    />
                </Col>
                <Col {...colSpan}>
                    <SingleSecretUploadField
                        {...t('moduleDetails:fields.publicCert', { returnObjects: true })}
                        name="publicCert"
                    />
                </Col>
                <Col {...colSpan}>
                    <SingleSecretUploadField
                        {...t('moduleDetails:fields.privateKey', { returnObjects: true })}
                        name="privateKey"
                    />
                </Col>
            </Row>
        </>
    );
};

const implementation: AudiFinancingModuleImplementation = {
    PageComponent: ({ module }: AudiFinancingModulePageProps) => <AudiFinancingModulePage module={module} />,
    getTitle: t => t('moduleDetails:implementations.AudiFinancingModule.className'),
    FormComponent: Form,
    getValidator: () => formValidator,
    create: async (apolloClient, companyId, { displayName, bankModuleId, ...secrets }) =>
        apolloClient
            .mutate<CreateAudiFinancingModuleMutation, CreateAudiFinancingModuleMutationVariables>({
                mutation: CreateAudiFinancingModuleDocument,
                variables: { companyId, settings: { displayName, bankModuleId }, secrets },
            })
            .then(({ data }) => data.module),
};

export default implementation;
